import { formatCurrency } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
  TemplateRef,
  viewChild,
  WritableSignal,
} from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import {
  AccountMarketplace,
  BillingPlan,
  Currency,
  OnboardingCallStatus,
  Organization,
  Plan,
} from "@front/m19-api-client";
import { CurrencySymbols, OrganizationAccountGroups } from "@front/m19-models";
import {
  AccountMarketplaceService,
  AuthService,
  BillingPlanService,
  BillingService,
  OrganizationAccountGroupService,
} from "@front/m19-services";
import { IButtonDirective, IIconComponent, ModalRef, ModalService, ToasterService } from "@front/m19-ui";
import { catchAjaxError } from "@front/m19-utils";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { OnboardingService, OnBoardingStepRedirects } from "@m19-board/services/onboarding.service";
import { catchError, combineLatest, filter, map, of, switchMap, take } from "rxjs";
import { SelectAccountsPageComponent } from "./select-accounts-page.component";

@Component({
  selector: "app-free-test-page",
  templateUrl: "./free-test-subscription-page.component.html",
  imports: [IButtonDirective, TranslocoDirective, SelectAccountsPageComponent, IIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FreeTestPageComponent {
  private readonly onboardingService = inject(OnboardingService);
  private readonly organizationAccountGroupService = inject(OrganizationAccountGroupService);
  private readonly translocoService = inject(TranslocoService);
  private readonly billingService = inject(BillingService);
  private readonly accountMarketplaceService = inject(AccountMarketplaceService);
  private readonly billingPlanService = inject(BillingPlanService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly modalService = inject(ModalService);
  private readonly toastrService = inject(ToasterService);
  readonly accountSelectionModalContent = viewChild<TemplateRef<any>>("accountSelection");
  readonly accountSelectionModalRef = signal<ModalRef<any> | undefined>(undefined);
  readonly activatingFreeTest = signal(false);
  readonly accountMarketplaces: WritableSignal<AccountMarketplace[]> = signal([]);
  readonly disabledAccountsMarketplaces = computed<Map<string, string | undefined>>(() =>
    this.getDisabledAccountsMarketplaces(this.accountMarketplaces()),
  );
  readonly selectedAccountMarketplaces = signal<AccountMarketplace[]>([]);
  private readonly destroyRef = inject(DestroyRef);
  private freeTestBillingPlan!: BillingPlan;
  readonly autopilotSuggestionMessage;
  readonly belowFreeTestAdSpendThreshold = toSignal(this.onboardingService.belowFreeTestAdSpendThreshold$);
  private readonly locale = toSignal(this.authService.userLocale$, { requireSync: true });
  private readonly autopilotEurPlan = toSignal(
    this.billingPlanService.listBillingPlans(Plan.AUTOPILOT).pipe(
      map((plans) => plans.find((p) => p.currency === Currency.EUR)),
      catchError(() => of(undefined)),
    ),
  );
  readonly autopilotMonthlyFee = computed(() => {
    const amount = this.autopilotEurPlan()?.flatFee?.amount;
    return amount === undefined
      ? undefined
      : formatCurrency(amount, this.locale(), CurrencySymbols[Currency.EUR], Currency.EUR, "1.0-0");
  });

  readonly organization = signal<Organization | undefined>(undefined);

  constructor() {
    this.autopilotSuggestionMessage = this.translocoService.translate("free-test-subscription-page.autopilot_ad_1", {
      betterFit: `<span class="font-semibold">${this.translocoService.translate("free-test-subscription-page.autopilot_could_be_a_better_fit_for_you")}</span>`,
    });
    combineLatest([
      this.organizationAccountGroupService.allOrganizationAccountGroups$.pipe(
        filter((o): o is OrganizationAccountGroups[] => !!o),
      ),
      this.onboardingService.belowFreeTestAdSpendThreshold$,
    ])
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(([organizationAccountGroups, belowFreeTestAdSpendThreshold]) => {
        if (organizationAccountGroups.length !== 1) {
          // free test activation is not possible in this case
          this.router.navigate(["/dashboard"]);
        }
        if (!this.onboardingService.entitledForFreeTestSubscription(organizationAccountGroups[0])) {
          // free test activation is not possible in this case
          this.router.navigate(["/dashboard"]);
        }
        if (
          !belowFreeTestAdSpendThreshold &&
          organizationAccountGroups[0].organization.onboardingCallStatus !== OnboardingCallStatus.DONE
        ) {
          // free test activation is not possible in this case
          this.router.navigate(["/dashboard"]);
        }
        this.organization.set(organizationAccountGroups![0].organization);
        this.accountMarketplaces.set(organizationAccountGroups![0].accountGroups.flatMap((g) => g.resources));
      });
    this.billingPlanService
      .listBillingPlans(Plan.FREE_TEST)
      .pipe(catchAjaxError())
      .subscribe({
        next: (plans) => (this.freeTestBillingPlan = plans[0]),
        error: (error) => {
          this.toastrService.error(error);
          this.router.navigate(["/dashboard"]);
        },
      });
  }

  skipOnboarding() {
    this.onboardingService.skipOnboarding();
    this.onboardingService.currentStep$.pipe(take(1)).subscribe((step) => {
      this.router.navigate([OnBoardingStepRedirects[step]]);
    });
  }

  private getDisabledAccountsMarketplaces(accountMarketplaces: AccountMarketplace[]) {
    const disabledAccountsMarketplaces = new Map<string, string | undefined>();
    for (const accountMarketplace of accountMarketplaces) {
      const key = `${accountMarketplace.accountId}.${accountMarketplace.marketplace}`;
      if (accountMarketplace.state === "BIDDER_ON") {
        disabledAccountsMarketplaces.set(
          key,
          this.translocoService.translate("billing-account-selection.this_account_is_part_of_your_current_plan"),
        );
      }
      if (!accountMarketplace.hasAccessToAdvertising) {
        disabledAccountsMarketplaces.set(key, this.translocoService.translate("billing-account-selection.access_lost"));
      }
      if (accountMarketplace.bidderOrganizationId) {
        disabledAccountsMarketplaces.set(
          key,
          this.translocoService.translate("billing-account-selection.accountanother_org_error"),
        );
      }
      if (!accountMarketplace.activated) {
        disabledAccountsMarketplaces.set(
          key,
          this.translocoService.translate("billing-account-selection.unable_to_automate", {
            amazonAdvertisingConsole: this.translocoService.translate("common.amazon_advertising_console"),
          }),
        );
      }
    }
    return disabledAccountsMarketplaces;
  }

  openFreeTestModal() {
    if (!this.accountMarketplaces()) return;
    const modalRef = this.modalService.openModal(this.accountSelectionModalContent()!, {
      modalTitle: this.translocoService.translate("onboarding.select_accounts_for_ads_automation"),
    });
    this.accountSelectionModalRef.set(modalRef);
  }

  activateFreeTest() {
    this.activatingFreeTest.set(true);
    this.billingService
      .createSubscription({ ...this.organization()!, billingPlan: this.freeTestBillingPlan })
      .pipe(
        catchAjaxError(),
        switchMap(() =>
          this.accountMarketplaceService.activateBidder(this.selectedAccountMarketplaces()).pipe(catchAjaxError()),
        ),
      )
      .subscribe({
        next: () => {
          this.activatingFreeTest.set(false);
          this.toastrService.success("Free test activated successfully");
          this.accountSelectionModalRef()?.close();
          this.router.navigate(["/dashboard"]);
        },
        error: (error) => {
          this.activatingFreeTest.set(false);
          this.toastrService.error(error, "Fail to activate free test");
        },
      });
  }
}
